import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useAnnounce } from './LiveRegion';

/**
 * 🔴 "SAVED." WITHOUT A BANNER THAT STAYS — design-system plan DS-6a (5.1; F519, F606, F625, F91).
 *
 * Forty-one screens kept a `notice` state and drew "Saved." as a green banner that sat above the form until the next
 * visit, pushing the form down. A confirmation is transient: `toast.success('Invoice INV-0042 posted.')` shows it for
 * five seconds — longer while the pointer is over it or focus is inside it — in a stack of at most three,
 * bottom-left on a desk and at the top on a phone. Each new toast is said once in the page's polite live region
 * (`useAnnounce`); the stack itself is a plain list, so a new toast does not re-read the ones before it (DS-6a
 * review). A refusal is not a toast: it stays on the screen as a `Banner tone="danger"` or an `ErrorState`, where it
 * can be read again.
 *
 * One `Toaster` per shell draws the stack; `toast` works from anywhere, a screen or a mutation's `onSuccess`.
 */
export type ToastTone = 'success' | 'info' | 'error';
export interface ToastItem {
  id: number;
  tone: ToastTone;
  text: string;
}

export const TOAST_MS = 5000;
const MAX = 3;

let items: ToastItem[] = [];
let next = 1;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

function push(tone: ToastTone, text: string): number {
  const id = next++;
  items = [...items, { id, tone, text }].slice(-MAX);
  emit();
  return id;
}

export function dismissToast(id: number): void {
  items = items.filter((t) => t.id !== id);
  emit();
}

export const toast = {
  success: (text: string) => push('success', text),
  info: (text: string) => push('info', text),
  error: (text: string) => push('error', text),
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function Toast({ item }: { item: ToastItem }) {
  // The pointer and the keyboard hold it separately: leaving with the pointer must not start the clock while focus is
  // still inside (DS-6a review — the toast went, and focus fell to the page).
  const hovered = useRef(false);
  const focused = useRef(false);
  const say = useAnnounce();
  useEffect(() => say(item.text), [item.text, say]);
  useEffect(() => {
    let left = TOAST_MS;
    let started = Date.now();
    const tick = setInterval(() => {
      const now = Date.now();
      if (!hovered.current && !focused.current) left -= now - started;
      started = now;
      if (left <= 0) dismissToast(item.id);
    }, 250);
    return () => clearInterval(tick);
  }, [item.id]);
  return (
    <li
      className={`wb-toast wb-toast--${item.tone}`}
      onMouseEnter={() => {
        hovered.current = true;
      }}
      onMouseLeave={() => {
        hovered.current = false;
      }}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) focused.current = false;
      }}
    >
      <span className="wb-toast__text">{item.text}</span>
      <button type="button" className="wb-toast__close" aria-label="Dismiss" onClick={() => dismissToast(item.id)}>
        ×
      </button>
    </li>
  );
}

export function Toaster() {
  const shown = useSyncExternalStore(subscribe, () => items, () => items);
  return (
    <ol className="wb-toaster" aria-label="Notifications">
      {shown.map((item) => (
        <Toast key={item.id} item={item} />
      ))}
    </ol>
  );
}

/** For specs: forget every toast. */
export function resetToasts(): void {
  items = [];
  emit();
}
